<template>
  <div class="container-fluid py-4">
    <!-- Dashboard Header - removed as per screenshot -->
    
    <!-- Summary Cards Row -->
    <div class="row mb-4">
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card border-0 shadow-sm h-100" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
          <div class="card-body text-white">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-white-50 mb-1">{{ $t('dashboard.totalProducts') }}</h6>
                <h2 class="mb-0">{{ summary?.total_products || 0 }}</h2>
              </div>
              <i class="fas fa-box fa-2x opacity-75"></i>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card border-0 shadow-sm h-100" style="background: linear-gradient(135deg, #28a745, #20c997);">
          <div class="card-body text-white">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-white-50 mb-1">{{ $t('dashboard.adataProducts') }}</h6>
                <h2 class="mb-0">{{ summary?.our_products || 0 }}</h2>
              </div>
              <i class="fas fa-star fa-2x opacity-75"></i>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card border-0 shadow-sm h-100" style="background: linear-gradient(135deg, #17a2b8, #007bff);">
          <div class="card-body text-white">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-white-50 mb-1">{{ $t('dashboard.competitiveProducts') }}</h6>
                <h2 class="mb-0">{{ summary?.competitor_products || 0 }}</h2>
              </div>
              <i class="fas fa-users fa-2x opacity-75"></i>
            </div>
          </div>
        </div>
      </div>
      
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card border-0 shadow-sm h-100" style="background: linear-gradient(135deg, #fd7e14, #ffc107);">
          <div class="card-body text-white">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h6 class="text-white-50 mb-1">{{ $t('dashboard.activeAlerts') }}</h6>
                <h2 class="mb-0">{{ summary?.active_alerts || 0 }}</h2>
              </div>
              <i class="fas fa-bell fa-2x opacity-75"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Cards Row -->
    <div class="row mb-4">
      <!-- ... existing summary cards ... -->
    </div>

    <!-- New Row for Price Change Monitoring and Competitor Inventory Status -->
    <div class="row mb-4">
      <!-- Price Change Monitoring (New Component) -->
      <div class="col-lg-6 col-md-12 mb-4">
        <div class="market-price-overview-section">
          <div class="card border-0 shadow-sm mb-3" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
            <div class="card-body text-white">
              <div class="d-flex align-items-center">
                <i class="fas fa-chart-line me-2"></i>
                <h5 class="mb-0">{{ $t('dashboard.priceChangeMonitoringTitle') }}</h5>
              </div>
            </div>
          </div>
          <PriceChangeMonitoring />
        </div>
      </div>

      <!-- Competitor Inventory Status Section (Existing) -->
      <div class="col-lg-6 col-md-12 mb-4">
        <div class="market-price-overview-section">
          <div class="card border-0 shadow-sm mb-3" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
            <div class="card-body text-white">
              <div class="d-flex align-items-center">
                <i class="fas fa-warehouse me-2"></i>
                <h5 class="mb-0">{{ $t('competitorInventoryStatus.title') }}</h5>
              </div>
            </div>
          </div>
          <CompetitorInventoryStatus />
        </div>
      </div>
    </div>

    <!-- Market Price Overview Section (Existing) -->
    <MarketPriceOverview />

    <!-- Brand Market Performance Summary Section -->
    <BrandMarketPerformanceSummary />

    <!-- Real-time Price Competitive Comparison -->
    <div class="row mb-4">
      <div class="col-12 mb-3">
        <!-- Real-time Price Competitive Comparison Header -->
        <div class="card border-0 shadow-sm mb-3" style="background: linear-gradient(135deg, #6f42c1, #8b5cf6);">
          <div class="card-body text-white">
            <div class="d-flex align-items-center">
              <i class="fas fa-sync-alt me-2"></i>
              <h5 class="mb-0">{{ $t('dashboard.realtimeComparison') }}</h5>
            </div>
          </div>
        </div>
        
        <!-- Real-time Price Competitive Comparison Content -->
        <div class="card border-0 shadow-sm">
          <div class="card-body">
            <p class="text-muted small mb-3">
              {{ $t('dashboard.comparisonDescription') }}
            </p>
            
            <!-- Loading State -->
            <div v-if="competitiveComparisonsLoading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ $t('dashboard.loading') }}</span>
              </div>
              <p class="text-muted mt-2">{{ $t('dashboard.loadingAnalysis') }}</p>
            </div>
            
            <!-- No Data State -->
            <div v-else-if="!competitiveComparisons || competitiveComparisons.length === 0" class="text-center py-4">
              <i class="fas fa-chart-line fa-3x text-muted mb-3"></i>
              <p class="text-muted">{{ $t('dashboard.noData') }}</p>
            </div>
            
            <!-- Summary Info -->
            <div v-if="competitiveComparisons && competitiveComparisons.length > 0" class="mt-3 pt-3 border-bottom mb-3">
              <div class="row text-center">
                <div class="col-md-3">
                  <small class="text-muted">{{ $t('dashboard.totalComparisons') }}</small>
                  <div class="fw-bold">{{ competitiveComparisonsTotal || competitiveComparisons.length }}</div>
                </div>
                <div class="col-md-3">
                  <small class="text-muted">{{ $t('dashboard.leading') }}</small>
                  <div class="fw-bold text-success">{{ leadingCount }}</div>
                </div>
                <div class="col-md-3">
                  <small class="text-muted">{{ $t('dashboard.competitive') }}</small>
                  <div class="fw-bold text-secondary">{{ competitiveCount }}</div>
                </div>
                <div class="col-md-3">
                  <small class="text-muted">{{ $t('dashboard.behind') }}</small>
                  <div class="fw-bold text-warning">{{ behindCount }}</div>
                </div>
              </div>
            </div>

            <!-- Competitive Comparison Table -->
            <div v-if="competitiveComparisons && competitiveComparisons.length > 0" class="table-responsive">
              <table class="table table-sm">
                <thead class="table-light">
                  <tr class="text-nowrap">
                    <th @click="sortByColumn('product_name')" class="cursor-pointer">
                      {{ $t('dashboard.productName') }}
                      <i v-if="currentSortBy === 'product_name'" 
                         :class="{'fas fa-sort-up': currentSortOrder === 'asc', 'fas fa-sort-down': currentSortOrder === 'desc'}"></i>
                      <i v-else class="fas fa-sort"></i>
                    </th>
                    <th @click="sortByColumn('adata_price')" class="cursor-pointer">
                      {{ $t('dashboard.adataPrice') }}
                      <i v-if="currentSortBy === 'adata_price'" 
                         :class="{'fas fa-sort-up': currentSortOrder === 'asc', 'fas fa-sort-down': currentSortOrder === 'desc'}"></i>
                      <i v-else class="fas fa-sort"></i>
                    </th>
                    <th>
                      {{ $t('dashboard.bspPrice') }}
                    </th>
                    <th @click="sortByColumn('competitor_price')" class="cursor-pointer">
                      {{ $t('dashboard.bestCompetitorPrice') }}
                      <i v-if="currentSortBy === 'competitor_price'" 
                         :class="{'fas fa-sort-up': currentSortOrder === 'asc', 'fas fa-sort-down': currentSortOrder === 'desc'}"></i>
                      <i v-else class="fas fa-sort"></i>
                    </th>
                    <th @click="sortByColumn('ranking')" class="cursor-pointer">
                      {{ $t('dashboard.ranking') }}
                      <i v-if="currentSortBy === 'ranking'" 
                         :class="{'fas fa-sort-up': currentSortOrder === 'asc', 'fas fa-sort-down': currentSortOrder === 'desc'}"></i>
                      <i v-else class="fas fa-sort"></i>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="comparison in competitiveComparisons" :key="comparison.adata_product.id">
                    <td>
                      <div class="d-flex align-items-center">
                        <i :class="comparison.adata_product.icon + ' me-2'"></i>
                        <div>
                          <span class="fw-medium">{{ comparison.adata_product.name }}</span>
                          <small class="text-muted d-block">
                            {{ formatCapacity(comparison.adata_product.storage_capacity) }} | 
                            {{ formatSpeed(comparison.adata_product.speed, comparison.adata_product.category) }}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="fw-bold text-success">
                        ${{ comparison.adata_product.price.toFixed(2) }}
                      </span>
                    </td>
                    <td>
                      <span class="fw-bold text-info">
                        ${{ comparison.adata_product.bsp_price ? comparison.adata_product.bsp_price.toFixed(2) : 'N/A' }}
                      </span>
                    </td>
                    <td>
                      <div>
                        <span class="fw-bold">${{ comparison.best_competitor.price.toFixed(2) }}</span>
                        <small class="text-muted d-block">{{ comparison.best_competitor.brand }}</small>
                      </div>
                    </td>
                    <td>
                      <span :class="'badge bg-' + comparison.comparison.position_badge">
                        <i :class="comparison.comparison.position_icon + ' me-1'"></i>
                        {{ formatPosition(comparison.comparison.position) }}
                      </span>
                      <small v-if="comparison.comparison.price_rank !== 0" 
                             :class="'d-block ' + (comparison.comparison.price_rank < 0 ? 'text-success' : 'text-danger')">
                        {{ comparison.comparison.price_rank > 0 ? '+' : '' }}{{ comparison.comparison.price_rank }}
                      </small>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Pagination Controls -->
            <nav v-if="totalPages > 1" aria-label="Page navigation" class="mt-3">
              <ul class="pagination justify-content-center">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">{{ $t('dashboard.previous') }}</a>
                </li>
                <li class="page-item" v-for="page in totalPages" :key="page" :class="{ active: currentPage === page }">
                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">{{ $t('dashboard.next') }}</a>
                </li>
              </ul>
            </nav>

          </div>
        </div>
      </div>
    </div>

    
    <p v-if="error" class="text-danger">{{ $t('dashboard.fetchError') }}</p>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import apiClient from '../api';
import MarketPriceOverview from './MarketPriceOverview.vue';
import BrandMarketPerformanceSummary from './BrandMarketPerformanceSummary.vue';
import CompetitorInventoryStatus from './CompetitorInventoryStatus.vue';
import PriceChangeMonitoring from './dashboard/PriceChangeMonitoring.vue';

const summary = ref(null);
// const competitiveAnalysis = ref({ competitive_comparison: [], brand_performance: [] });
const competitiveComparisons = ref([]);
const competitiveComparisonsLoading = ref(false);
const competitiveComparisonsTotal = ref(0);
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = ref(1);
const currentSortBy = ref(null);
const currentSortOrder = ref(null);
const leadingCount = ref(0);
const competitiveCount = ref(0);
const behindCount = ref(0);
const error = ref(null);
const lastUpdatedTime = ref(new Date().toLocaleTimeString());

const formatCapacity = (capacity) => {
  if (!capacity) return 'N/A';
  if (capacity >= 1024) {
    return `${(capacity / 1024).toFixed(0)}GB`;
  }
  return `${capacity}MB`;
};

const formatSpeed = (speed, category) => {
  if (!speed) return 'N/A';
  if (category === 'DRAM') {
    return `${speed}MHz`;
  } else if (category === 'SSD') {
    return `${speed}MB/s`;
  }
  return `${speed}`;
};

const formatPosition = (position) => {
  const positions = {
    'leading': 'Leading',
    'competitive': 'Competitive', 
    'behind': 'Behind'
  };
  return positions[position] || position;
};



const fetchCompetitiveComparisons = async () => {
  competitiveComparisonsLoading.value = true;
  try {
    const offset = (currentPage.value - 1) * itemsPerPage.value;
    const response = await apiClient.get(`/ci/realtime_competitive_comparison?limit=${itemsPerPage.value}&offset=${offset}${currentSortBy.value ? `&sort_by=${currentSortBy.value}` : ''}${currentSortOrder.value ? `&sort_order=${currentSortOrder.value}` : ''}`);
    if (response.data && response.data.success) {
      competitiveComparisons.value = response.data.data || [];
      competitiveComparisonsTotal.value = response.data.total_comparisons || 0;
      totalPages.value = Math.ceil(competitiveComparisonsTotal.value / itemsPerPage.value);
      leadingCount.value = response.data.leading_count || 0;
      competitiveCount.value = response.data.competitive_count || 0;
      behindCount.value = response.data.behind_count || 0;
    }
  } catch (err) {
    console.error('Failed to fetch competitive comparisons:', err);
    competitiveComparisons.value = [];
  } finally {
    competitiveComparisonsLoading.value = false;
  }
};

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchCompetitiveComparisons();
  }
};

const sortByColumn = (column) => {
  if (currentSortBy.value === column) {
    currentSortOrder.value = currentSortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    currentSortBy.value = column;
    currentSortOrder.value = 'asc';
  }
  fetchCompetitiveComparisons();
};

const fetchAllData = async () => {
  error.value = null;
  try {
    // Fetch all data in parallel with proper error handling
    const [ciKpisRes, alertsRes] = await Promise.all([
      apiClient.get('/ci/kpis').catch(err => ({ data: null, error: err })),
      apiClient.get('/ci/alerts?limit=5').catch(err => ({ data: [], error: err })),
      // apiClient.get('/ci/price_comparison?page=1&page_size=5').catch(err => ({ data: null, error: err }))
    ]);
    
    // Process CI KPIs
    if (ciKpisRes.data) {
      summary.value = {
        total_products: ciKpisRes.data.total_products_monitored,
        our_products: ciKpisRes.data.our_products, // ADATA products count
        competitor_products: ciKpisRes.data.competitor_products, // Competitor products count
        active_alerts: alertsRes.data ? alertsRes.data.length : 0,
        unread_notifications: alertsRes.data ? alertsRes.data.filter(a => a.type <= 1).length : 0,
        competitive_coverage_rate: ciKpisRes.data.competitive_coverage_rate,
        total_competitive_quotes: ciKpisRes.data.total_competitive_quotes
      };
    }
    
    lastUpdatedTime.value = new Date().toLocaleTimeString();

    // Fetch competitive comparisons separately
    await fetchCompetitiveComparisons();

  } catch (err) {
    error.value = 'Failed to fetch dashboard data. Please check your connection.';
    console.error('Dashboard fetch error:', err);
  }
};




onMounted(() => {
  fetchAllData();
});



</script>

<style scoped>
.card {
  transition: transform 0.2s ease-in-out;
}

.card:hover {
  transform: translateY(-2px);
}

.opacity-75 {
  opacity: 0.75;
}

.badge {
  font-size: 0.75rem;
}
</style>